import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

export interface VirtualizedDateListItem {
  utcDate: string;
  longDate: string;
}

@Component({
  selector: 'app-virtualized-date-list',
  templateUrl: './virtualized-date-list.component.html',
  styleUrls: ['./virtualized-date-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class VirtualizedDateListComponent implements OnChanges {
  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;

  @Input() public dates: ReadonlyArray<VirtualizedDateListItem> | null = [];
  @Input() public selectedDate: string | null = null;
  @Input() public ariaLabel = 'Date selector';
  @Output() public readonly dateSelected = new EventEmitter<VirtualizedDateListItem>();

  public readonly itemSize = 48;
  public readonly minBufferPx = this.itemSize * 5;
  public readonly maxBufferPx = this.itemSize * 10;
  public activeIndex = 0;

  public get renderedDates(): ReadonlyArray<VirtualizedDateListItem> {
    return this.dates ?? [];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dates'] || changes['selectedDate']) {
      this.syncActiveIndex();
    }
  }

  public selectDate(date: VirtualizedDateListItem, index: number): void {
    this.activeIndex = index;
    this.dateSelected.emit(date);
  }

  public onFocus(index: number): void {
    this.activeIndex = index;
  }

  public onKeydown(event: KeyboardEvent): void {
    const totalDates = this.renderedDates.length;

    if (totalDates === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActiveIndex(Math.min(this.activeIndex + 1, totalDates - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActiveIndex(Math.max(this.activeIndex - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActiveIndex(totalDates - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.renderedDates[this.activeIndex]) {
          this.selectDate(this.renderedDates[this.activeIndex], this.activeIndex);
        }
        break;
    }
  }

  public trackByDate(_: number, date: VirtualizedDateListItem): string {
    return date.utcDate;
  }

  public getOptionId(index: number): string {
    return `virtualized-date-option-${index}`;
  }

  private syncActiveIndex(): void {
    if (this.renderedDates.length === 0) {
      this.activeIndex = 0;
      return;
    }

    const selectedIndex = this.selectedDate
      ? this.renderedDates.findIndex(date => date.utcDate === this.selectedDate)
      : -1;

    this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
  }

  private setActiveIndex(index: number): void {
    this.activeIndex = index;
    this.viewport?.scrollToIndex(index, 'auto');

    setTimeout(() => {
      const activeOption = this.viewport?.elementRef.nativeElement.querySelector(`#${this.getOptionId(index)}`) as HTMLElement | null;
      activeOption?.focus();
    });
  }
}
